/* src/components/ResetPassword.js - v1.2 - Post-Reset App Handoff Popup
   CHANGELOG (v1.1 -> v1.2): Replaced the pre-form "Open in App" button
   (sat above the password fields, before the reset even happened - user
   feedback was that it looked out of place and duplicated the form's own
   job) with a popup shown AFTER a successful reset, mobile only: "Open
   the App" or "Continue on Web". This is simpler too - the app-side deep
   link now just opens the bare scheme (nubixsocial://) rather than
   nubixsocial://reset-password?token=..., so it lands wherever the app
   normally would (the login screen, since the user isn't authenticated
   yet) instead of re-entering ResetPasswordScreen with an already-used
   token. No mobile-side changes needed for this - App.js's deep-link
   listener already no-ops (falls through to the normal Login/AppNavigator
   branch) when the URL has no reset-password token.
   CHANGELOG (v1.0 -> v1.1): The email link is necessarily a plain
   https:// URL (custom URL schemes are generally stripped/blocked by
   email clients), so it always lands here in the browser even when the
   user requested the reset from the mobile app - after finishing here,
   they were stuck on the website with no way back into the app. */
import React, { useState, useEffect } from 'react';

const isMobileDevice = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

function ResetPassword({ onComplete, backgroundStyle }) {
  const [token, setToken] = useState('');
  const [username, setUsername] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showAppPrompt, setShowAppPrompt] = useState(false);

  // 1. Get Token & Verify on Mount
  useEffect(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const t = urlParams.get('token');
      setToken(t);

      if (!t) {
          setError("No token provided.");
          setLoading(false);
          return;
      }

      // Verify Token with Backend
      fetch('/api/reset-password/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: t })
      })
      .then(res => res.json())
      .then(data => {
          if (data.valid) {
              setUsername(data.username);
          } else {
              setError("Link expired or invalid.");
          }
      })
      .catch(() => setError("Connection error."))
      .finally(() => setLoading(false));
  }, []);

  const handleReset = async (e) => {
      e.preventDefault();
      if (newPassword !== confirmPassword) return alert("Passwords do not match!");
      if (newPassword.length < 3) return alert("Password too short!");

      try {
          const res = await fetch('/api/reset-password', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token, password: newPassword })
          });
          const data = await res.json();
          
          if (res.ok) {
              if (isMobileDevice) {
                  setShowAppPrompt(true);
              } else {
                  alert("Password changed! Please log in.");
                  onComplete(); // Go to Login
              }
          } else {
              alert(data.error || "Failed to reset.");
          }
      } catch (err) { console.error(err); }
  };

  return (
    <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', ...backgroundStyle }}>
      <div className="card" style={{ width: '100%', maxWidth: '400px', padding: '40px', textAlign: 'center' }}>
        
        <h2 style={{ marginBottom: '20px' }}>Reset Password</h2>

        {loading && <p>Verifying link...</p>}

        {error && (
            <div>
                <p style={{ color: '#ff4757', marginBottom: '20px' }}>{error}</p>
                <button onClick={onComplete} className="action-btn">Return to Login</button>
            </div>
        )}

        {!loading && !error && username && (
            <form onSubmit={handleReset} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '10px' }}>
                    Setting new password for: <strong style={{ color: 'white' }}>{username}</strong>
                </p>

                <input
                    type="password" 
                    className="post-input" 
                    placeholder="New Password" 
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                />
                <input 
                    type="password" 
                    className="post-input" 
                    placeholder="Confirm Password" 
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                />
                
                <button type="submit" style={{ marginTop: '10px', padding: '12px', background: '#2ed573', color: 'white', border: 'none', borderRadius: '5px', fontWeight: 'bold', cursor: 'pointer' }}>
                    Update Password
                </button>
                
                <button type="button" onClick={onComplete} style={{ background: 'transparent', border: 'none', color: '#aaa', cursor: 'pointer', marginTop: '10px' }}>
                    Cancel
                </button>
            </form>
        )}
      </div>

      {showAppPrompt && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }}>
              <div className="card" style={{ width: '90%', maxWidth: '360px', padding: '30px', textAlign: 'center' }}>
                  <h3 style={{ color: 'white', marginTop: 0, marginBottom: '10px' }}>Password Changed</h3>
                  <p style={{ color: '#aaa', fontSize: '0.9rem', marginBottom: '25px' }}>
                      Your password has been updated. Open the Nubix app to log in, or continue here on the web.
                  </p>
                  <a
                      href="nubixsocial://"
                      style={{ display: 'block', padding: '12px', marginBottom: '12px', background: 'linear-gradient(45deg, #ff4757, #ff6b81)', color: 'white', textDecoration: 'none', borderRadius: '30px', fontWeight: 'bold' }}
                  >
                      Open the App
                  </a>
                  <button
                      type="button"
                      onClick={onComplete}
                      style={{ width: '100%', padding: '12px', background: 'transparent', border: '1px solid #444', color: '#ccc', borderRadius: '30px', fontWeight: 'bold', cursor: 'pointer' }}
                  >
                      Continue on Web
                  </button>
              </div>
          </div>
      )}
    </div>
  );
}

export default ResetPassword;
